import React, { useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Axios from 'axios';
import { useTranslation } from 'react-i18next';

import {
    Button, Grid,
    Tooltip
} from '@mui/material';

import { appendContactSupport, axiosHeaders, getLocalisedErrorString } from 'src/utils/helpers';
import ConfigContext from 'src/contexts/ConfigContext';
import styles from '../style';
import { useAuth } from 'src/hooks/useAuth';
import { GDriveDataType } from 'src/types';
import {
    HelpOutline as HelpOutlineIcon,
    Save,
} from '@mui/icons-material';

interface GDrivePostApiDataType {
    folderName: string;
    folderId: string;
}

interface IGoogleDriveIntegrationProps {
    isIntegrationActive: boolean;
}

const GoogleDriveIntegration = (props: IGoogleDriveIntegrationProps) => {
    const { isIntegrationActive } = props;
    const { API } = useContext(ConfigContext);
    const { ready, t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [savedFolderName, setSavedFolderName] = useState<boolean>(false);
    const [savedFolderId, setSavedFolderId] = useState<boolean>(false);
    const [gDriveData, setGDriveData] = useState<GDriveDataType | null>(null);
    const [formData, setFormData] = useState<GDrivePostApiDataType>({
        folderName: '',
        folderId: ''
    });

    const getSavedDetails = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                `${API.savedDetailsGDrive}/${user?.companyID}`,
                axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
            );
            const data = response?.data?.data;

            if (response.data.success && data) {
                setGDriveData(data);
                setFormData({
                    folderName: data.folderName || '',
                    folderId: data.folderId || ''
                });
            } else {
                enqueueSnackbar(t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS'), {
                    variant: 'error',
                    autoHideDuration: 5000
                });
            }
        } catch (err: any) {
            const errMsg = err?.response?.data
                ? getLocalisedErrorString(err.response.data.i18n, t)
                : t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS');
            enqueueSnackbar(
                appendContactSupport(window.config.support_email, errMsg, t),
                {
                    variant: 'error',
                    autoHideDuration: 5000
                }
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isIntegrationActive) {
            getSavedDetails();
        }
    }, [isIntegrationActive]);

    const onChangeGDriveDetails = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const onCallSaveGDriveDetails = async () => {
        setLoading(true);
        try {
            const payload: GDrivePostApiDataType = {
                folderName: formData.folderName,
                folderId: formData.folderId.trim(),
            };

            const response = await Axios.post(
                `${API.savedDetailsGDrive}/${user?.companyID}`,
                payload,
                axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
            );

            if (response.data.success) {
                setSavedFolderName(!!payload.folderName);
                setSavedFolderId(!!payload.folderId);
                enqueueSnackbar(t('GOOGLE_DRIVE_INTEGRATION_SAVE_SUCCESS'), {
                    variant: 'success',
                    autoHideDuration: 5000
                });
                await getSavedDetails();
            } else {
                enqueueSnackbar(t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS'), {
                    variant: 'error',
                    autoHideDuration: 5000
                });
            }
        } catch (err: any) {
            const errMsg = err?.response?.data
                ? getLocalisedErrorString(err.response.data.i18n, t)
                : t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS');
            enqueueSnackbar(
                appendContactSupport(window.config.support_email, errMsg, t),
                {
                    variant: 'error',
                    autoHideDuration: 5000
                }
            );
        } finally {
            setLoading(false);
        }
    };

    const onOAuth2Connect = (oauth2URL: string) => {
        window.open(oauth2URL, '_blank');
    };

    const onCheckConnection = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                `${API.testGDriveConnection}/${user?.companyID}`,
                axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
            );
            if (response.data.success) {
                setSavedFolderName(false);
                setSavedFolderId(false);
                enqueueSnackbar(t('INTEGRATIONS_GSTOCK_CONN_SUCCESS'), {
                    variant: 'success',
                    autoHideDuration: 5000
                });
            } else {
                enqueueSnackbar(
                    appendContactSupport(window.config.support_email, t('SOMETHING_WENT_WRONG'), t),
                    {
                        variant: 'error',
                        autoHideDuration: 5000
                    }
                );
            }
        } catch (error: any) {
            const errMsg = error?.response?.data
                ? getLocalisedErrorString(error.response.data.i18n, t)
                : t('SOMETHING_WENT_WRONG');
            enqueueSnackbar(
                appendContactSupport(window.config.support_email, errMsg, t),
                {
                    variant: 'error',
                    autoHideDuration: 5000
                }
            );
        } finally {
            setLoading(false);
        }
    };

    if (loading && gDriveData === null) {
        return (
            <Grid sx={styles.errorContainer}>
                <span style={styles.labelText}>{ready && t('LOADING_PAGE_TEXT')}</span>
            </Grid>
        );
    }

    if (!gDriveData) {
        return null;
    }

    const isSaveDisabled =
        loading ||
        (!formData.folderName && !formData.folderId) ||
        (
            formData.folderName === gDriveData.folderName &&
            formData.folderId.trim() === gDriveData.folderId?.trim()
        );

    return (
        <Grid sx={styles.detailsContainer}>
            {gDriveData?.needsAuthorization ? (
                <Grid sx={styles.gridDetailRow}>
                    <span style={styles.qbConnectText}>{ready && t('GOOGLE_DRIVE_CLICK_HERE')}</span>
                    <Tooltip
                        title={t('TOOLTIP_CONNECT_AUTH_BTN')}
                        disableHoverListener={gDriveData?.isAdmin}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                sx={styles.qbConnectBtn}
                                onClick={() => onOAuth2Connect(gDriveData.oauth2URL!)}
                                disabled={loading || gDriveData?.isAdmin === false}
                            >
                                {ready && t('GOOGLE_DRIVE_CONNECT')}
                            </Button>
                        </span>
                    </Tooltip>
                </Grid>
            ) : (
                <>
                    <Grid sx={styles.gridDetailRow}>
                        <Grid sx={styles.labelWrapper}>
                            <div style={styles.labelTooltipWrapper}>
                                <span style={styles.labelText}>{ready && t('INTEGRATIONS_GDRIVE_FOLDER_NAME')}</span>
                                <Tooltip title={t('TOOLTIP_GDRIVE_FOLDER_NAME')}>
                                    <HelpOutlineIcon style={styles.interfaceIcon} />
                                </Tooltip>
                            </div>
                            {savedFolderName && (
                                <Grid style={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Grid>
                            )}
                        </Grid>
                        <input
                            type="text"
                            name="folderName"
                            value={formData.folderName}
                            style={styles.inputField}
                            onChange={onChangeGDriveDetails}
                        />
                    </Grid>
                    <Grid sx={styles.gridDetailRow}>
                        <Grid sx={styles.labelWrapper}>
                            <span style={styles.labelText}>{ready && t('INTEGRATIONS_GDRIVE_FOLDER_ID')}</span>
                            {savedFolderId && (
                                <Grid sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Grid>
                            )}
                        </Grid>
                        <input
                            type="text"
                            name="folderId"
                            value={formData.folderId}
                            style={styles.inputField}
                            onChange={onChangeGDriveDetails}
                        />
                    </Grid>
                    <div style={styles.btnsWrapper}>
                        {formData.folderId && formData.folderName ? (
                            <Button
                                variant="outlined"
                                sx={styles.checkConnectionBtn}
                                onClick={onCheckConnection}
                                disabled={loading}
                            >
                                {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                            </Button>
                        ) : (
                            <div />
                        )}
                        <Button
                            variant="outlined"
                            sx={styles.checkConnectionBtn}
                            onClick={() => onCallSaveGDriveDetails()}
                            disabled={isSaveDisabled}
                            startIcon={<Save />}
                        >
                            {ready && t('CUSTOMER_INTEGRATIONS_SAVE')}
                        </Button>
                    </div>
                </>
            )}
        </Grid>
    );
}

export default GoogleDriveIntegration;